import * as React from "react";
import cx from "classnames";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
} from "@floating-ui/react";
import type { Placement } from "@floating-ui/react";

import "./Tooltip.scss";

const DEFAULT_TOOLTIP_OPEN_DELAY = 300;
const DEFAULT_TOOLTIP_CLOSE_DELAY = 200;

interface TooltipOptions {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  tooltipRole?: string;
  multiline?: boolean;
}

function useTooltip({
  initialOpen = false,
  placement = "top",
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  tooltipRole = "tooltip",
  multiline = false,
}: TooltipOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const arrowRef = React.useRef(null);

  // For multiline tooltips, default to top-end to position at top-right of trigger
  const resolvedPlacement =
    multiline && placement === "top" ? ("top-end" as Placement) : placement;

  const data = useFloating({
    placement: resolvedPlacement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: resolvedPlacement.includes("-"),
        fallbackAxisSideDirection: "start",
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  const context = data.context;

  const hoverInteraction = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
    delay: {
      open: DEFAULT_TOOLTIP_OPEN_DELAY,
      close: DEFAULT_TOOLTIP_CLOSE_DELAY,
    },
  });
  const focusInteraction = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismissInteraction = useDismiss(context);
  const roleInteraction = useRole(context, { role: tooltipRole as any });

  const interactions = useInteractions([
    hoverInteraction,
    focusInteraction,
    dismissInteraction,
    roleInteraction,
  ]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      multiline,
      ...interactions,
      ...data,
    }),
    [open, setOpen, arrowRef, multiline, interactions, data],
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = React.createContext<ContextType>(null);

const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);

  if (context == null) {
    throw new Error("Tooltip components must be wrapped in <Tooltip />");
  }

  return context;
};

export const Tooltip = React.memo(
  ({
    children,
    ...options
  }: { children: React.ReactNode } & TooltipOptions) => {
    // This can accept any props as options, e.g. `placement`,
    // or other positioning options.
    const tooltip = useTooltip(options);
    return (
      <TooltipContext.Provider value={tooltip}>
        {children}
      </TooltipContext.Provider>
    );
  },
);

export const TooltipTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger(
  { children, className, asChild = false, ...props },
  propRef,
) {
  const context = useTooltipContext();
  // In React 19, ref is a regular prop, not a special property on the element
  const childrenRef = React.isValidElement(children)
    ? (children.props as any).ref
    : null;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    const childProps =
      typeof children.props === "object" && children.props !== null
        ? children.props
        : {};
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ...props,
        ...childProps,
        // `ref` must come after `...childProps`: in React 19 the child's own
        // ref lives on `children.props.ref`, so spreading childProps last would
        // clobber our merged ref and drop `setReference`, leaving the tooltip
        // unanchored (it never positions/shows). `ref` already merges the
        // child's ref via useMergeRefs, so nothing is lost by overriding here.
        ref,
        "data-state": context.open ? "open" : "closed",
      } as React.HTMLAttributes<Element>),
    );
  }

  const classes = cx("TooltipTrigger", className);

  return (
    <div
      className={classes}
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? "open" : "closed"}
      {...context.getReferenceProps(props)}
    >
      {children}
    </div>
  );
});

export const TooltipContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(function TooltipContent({ style, className, children, ...props }, propRef) {
  const context = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  const classes = cx("TooltipContent", className);

  if (!context.open) {
    return null;
  }

  const tooltipStyle = {
    ...context.floatingStyles,
    ...(context.multiline ? { whiteSpace: "pre-line" as const } : {}),
    ...style,
  };

  return (
    <FloatingPortal>
      <div
        className={classes}
        ref={ref}
        style={tooltipStyle}
        {...context.getFloatingProps(props)}
      >
        {children}
      </div>
    </FloatingPortal>
  );
});
